import React, { useEffect, useState } from 'react';
import { IUser } from '../../models/IUser';
import {
    TextField,
    Button,
    Typography,
    Container,
    Grid,
    Avatar,
    Box,
    Paper
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import UserServiceInstance from '../../services/UserService';
import axios from 'axios';
import OntimeBreadcrumb from '../common/OntimeBreadcrumb';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            maxWidth: '36ch',
            backgroundColor: theme.palette.background.paper,
        },
        inline: {
            display: 'inline',
        },
        content: {
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
        },
        container: {
            paddingTop: theme.spacing(4),
            paddingBottom: theme.spacing(4),
        },
        paper: {
            padding: theme.spacing(2),
            display: 'flex',
            overflow: 'auto',
            flexDirection: 'column',
        },
        appBarSpacer: theme.mixins.toolbar
    }));

function Profile() {
    const [userProfile, setUserProfile] = useState<IUser>();

    const { t } = useTranslation("common");
    const classes = useStyles();

    useEffect(() => {
        const source = axios.CancelToken.source();
        const fetchData = async () => {
            const user = await UserServiceInstance.getLoggedInUser(source.token);
            setUserProfile(user);
        };
        fetchData().catch((error) => {
            if (!axios.isCancel(error)) {
                console.error("Error fetching user data:", error);
            }
        });
        return () => {
            source.cancel();
        };
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        if (userProfile) {
            setUserProfile({ ...userProfile, [name]: value });
        }
    };

    const handlePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (userProfile && e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.onloadend = () => {
                console.log('File loaded:', reader.result);
                setUserProfile({ ...userProfile, picture: reader.result as string });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        console.log('User Profile Updated:', userProfile);
        // Add logic to save the updated profile
        if (userProfile) {
            UserServiceInstance.updateUserProfile(userProfile).then(() => {
            }).catch((error: any) => {
                console.error("Error updating user profile:", error);
            });
        }
    };

    return (
        (userProfile ?
            <main className={classes.content}>
                <div className={classes.appBarSpacer} />
                <Container maxWidth="sm" className={classes.container}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <OntimeBreadcrumb nameMap={{}} current={t('profile.title')} />
                        </Grid>
                        <Grid item xs={12}>
                            <Paper className={classes.paper}>
                                <Typography variant="h4" component="h1" gutterBottom>
                                    {t('profile.title')}
                                </Typography>
                                <form onSubmit={handleSubmit}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label={t('profile.email')}
                                                value={userProfile.email ?? ''}
                                                variant="outlined"
                                                InputProps={{ readOnly: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label={t('profile.displayName')}
                                                name="displayName"
                                                value={userProfile.name ?? ''}
                                                onChange={handleInputChange}
                                                variant="outlined"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label={t('profile.personalWebPage')}
                                                name="personalWebPage"
                                                value={userProfile.webPage ?? ''}
                                                onChange={handleInputChange}
                                                variant="outlined"
                                                type="url"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label={t('profile.comment')}
                                                name="summary"
                                                value={userProfile.comment ?? ''}
                                                onChange={handleInputChange}
                                                variant="outlined"
                                                multiline
                                                rows={4}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box display="flex" alignItems="center" gap={2}>
                                                <Button variant="contained" component="label">
                                                    {t('profile.uploadPicture')}
                                                    <input
                                                        type="file"
                                                        hidden
                                                        accept="image/*"
                                                        onChange={handlePictureChange}
                                                    />
                                                </Button>
                                                {(userProfile.picture || userProfile.pictureUrl) && (
                                                    <Avatar
                                                        src={userProfile.picture || userProfile.pictureUrl}
                                                        alt="Profile"
                                                        sx={{ width: 56, height: 56 }}
                                                    />
                                                )}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                            >
                                                {t('commonActions.save')}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </form>
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </main> : <main></main>));
}

export default Profile;
